import EndorseEditor from "components/endorse";
import { Container, Loader } from "components";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { IPaymentImage } from "interfaces/IPaymentImageResponse";
import { IPayment, EndorsementPositionRequest } from "interfaces";
import { getCompanyAccountDetails, getPaymentImage } from "api";

export interface IStateProps {
  payment: IPayment | undefined;
  backImage: IPaymentImage | undefined;
  frontImage: IPaymentImage | undefined;
  refresh?: boolean;
  imagesCropped?: boolean;
  accountId?: any;
  companyId?: any;
  accountType?: "deluxe" | "receivable";
  onSuccessCallback?: (params: any) => void;
  setEndorsement?: (params: any) => void;
  isEndorseRequired?: (params: any) => void;
  isRotated?: (params: boolean) => void;
}

const Endorsement: React.FunctionComponent<IStateProps> = (props) => {
  const {
    payment,
    backImage,
    refresh,
    accountId,
    companyId,
    accountType,
    imagesCropped,
    onSuccessCallback,
    setEndorsement,
    isEndorseRequired,
    isRotated,
  } = props;

  const [endorsementText, setEndorsementText] = useState("");
  const [loading, setLoading] = useState(true);
  const [backImageToEdit, setBackImagetoEdit] = useState(backImage);
  const [base64Image, setBase64Image] = useState(
    `data:image/png;base64,${backImage?.base64Image}`
  );

  // Keep unstable parent callbacks out of effect deps so Preview endorsement
  // (setEndorsement → parent re-render) does not remount EndorseEditor.
  const isEndorseRequiredRef = useRef(isEndorseRequired);
  isEndorseRequiredRef.current = isEndorseRequired;

  const loadCroppedBackImage = useCallback(() => {
    if (!payment?.id) {
      setLoading(false);
      return;
    }
    getPaymentImage(payment.id)
      .then((image) => {
        if (image) {
          const back = image.data.images
            ?.filter((i) => i.imageIndex === "BACK")
            .pop();
          if (back) {
            setBackImagetoEdit(back);
            setBase64Image(`data:image/png;base64,${back.base64Image}`);
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [payment?.id]);

  useEffect(() => {
    setLoading(true);

    // Receivable / Passport: preview uses receivableAccountId — skip company-account.
    if (accountType === "receivable") {
      if (imagesCropped) {
        loadCroppedBackImage();
      } else {
        setLoading(false);
      }
      return;
    }

    const resolvedAccountId = accountId || payment?.accountId;
    const resolvedCompanyId = companyId || payment?.companyId;
    if (!resolvedAccountId || !resolvedCompanyId) {
      setLoading(false);
      return;
    }

    getCompanyAccountDetails(resolvedCompanyId, resolvedAccountId)
      .then((companyAccount) => {
        setEndorsementText(
          `${companyAccount.data.endorsement}\n${payment?.companyName}\n${companyAccount.data.accountNo}`
        );
        isEndorseRequiredRef.current?.(companyAccount.data.endorsementRequired);
      })
      .finally(() => {
        if (!imagesCropped) {
          setLoading(false);
        }
      });

    if (imagesCropped) {
      loadCroppedBackImage();
    }
    // Intentionally omit isEndorseRequired — see isEndorseRequiredRef above.
  }, [
    accountType,
    accountId,
    companyId,
    payment?.id,
    payment?.accountId,
    payment?.companyId,
    payment?.companyName,
    imagesCropped,
    loadCroppedBackImage,
  ]);

  return (
    <Container>
      {loading ? (
        <Loader loading={loading} type={"default"} height="35vh"></Loader>
      ) : (
        <EndorseEditor
          refresh={refresh}
          endorsementText={endorsementText}
          base64Image={base64Image}
          backImage={backImageToEdit}
          imageId={backImage?.id || 0}
          accountId={accountId || payment?.accountId}
          accountType={accountType}
          onSuccessCallback={(params: EndorsementPositionRequest) =>
            onSuccessCallback?.(params)
          }
          isRotated={(params: boolean) => isRotated?.(params)}
          setEndorsement={(params: any) => setEndorsement?.(params)}
        />
      )}
    </Container>
  );
};

export default Endorsement;
